/**
 * MineRadio playback queue model.
 *
 * Adapted from YesPlayMusic v0.4.11 `src/utils/Player.js` (MIT License,
 * Copyright (c) 2020-2023 qier222; see the root `LICENSE` and
 * `THIRD_PARTY_NOTICES` for the retained notice). The upstream model separates the active
 * list/current index from the one-shot `playNext` list. This version keeps
 * stable string IDs and leaves source resolution and audio lifecycle to the
 * surrounding controller.
 */

export type QueueRepeatMode = 'sequence' | 'repeat-all' | 'repeat-one';

const unique = (ids: readonly string[]) => {
  const seen = new Set<string>();
  return ids.filter((id) => Boolean(id) && !seen.has(id) && seen.add(id));
};

const sameIds = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

const shuffle = <T>(items: readonly T[], random: () => number): T[] => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
};

export type QueueNextOptions = {
  direction?: 1 | -1;
  cause?: 'manual' | 'ended';
  mode?: QueueRepeatMode;
  shuffle?: boolean;
};

export class PlaybackQueue {
  private listIds: string[] = [];
  private currentId: string | null = null;
  private playNextIds: string[] = [];
  private shuffledIds: string[] = [];
  private shuffledCurrent = -1;
  private lastReturnedId: string | null = null;
  private random: () => number;

  public constructor(random: () => number = Math.random) {
    this.random = random;
  }

  public setList(ids: readonly string[], currentId: string | null = this.currentId): void {
    const nextList = unique(ids);
    const listChanged = !sameIds(this.listIds, nextList);
    this.listIds = nextList;
    const nextCurrent = currentId && this.listIds.includes(currentId) ? currentId : null;
    if (listChanged) {
      this.currentId = nextCurrent;
      this.lastReturnedId = null;
      this.rebuildShuffle();
      return;
    }
    if (nextCurrent !== this.currentId) this.setCurrent(nextCurrent);
  }

  public setCurrent(id: string | null): void {
    this.currentId = id && this.listIds.includes(id) ? id : null;
    const shuffledIndex = this.shuffledIds.indexOf(this.currentId || '');
    if (shuffledIndex >= 0) {
      if (this.currentId && this.currentId !== this.lastReturnedId) {
        this.shuffledIds = [this.currentId, ...this.shuffledIds.filter((item) => item !== this.currentId)];
        this.shuffledCurrent = 0;
      } else {
        this.shuffledCurrent = shuffledIndex;
      }
    }
    this.lastReturnedId = null;
  }

  public getCurrent(): string | null {
    return this.currentId;
  }

  public getList(): string[] {
    return [...this.listIds];
  }

  public setPlayNext(ids: readonly string[]): void {
    this.playNextIds = unique(ids).filter((id) => id !== this.currentId);
  }

  public getPlayNext(): string[] {
    return [...this.playNextIds];
  }

  public enqueueNext(id: string): boolean {
    if (!id || id === this.currentId || this.playNextIds.includes(id)) return false;
    this.playNextIds.push(id);
    return true;
  }

  public removePlayNext(id: string): boolean {
    const before = this.playNextIds.length;
    this.playNextIds = this.playNextIds.filter((item) => item !== id);
    return before !== this.playNextIds.length;
  }

  public clearPlayNext(): void {
    this.playNextIds = [];
  }

  /**
   * Match YesPlayMusic's `_getNextTrack`/`_getPrevTrack` behavior while
   * keeping repeat-one explicit for natural ends only.
   */
  public next(options: QueueNextOptions = {}): string | null {
    const direction = options.direction ?? 1;
    const cause = options.cause ?? 'manual';
    const mode = options.mode ?? 'sequence';
    const useShuffle = options.shuffle === true && direction > 0;
    if (direction > 0 && this.playNextIds.length) {
      return this.playNextIds.shift() || null;
    }

    if (!this.listIds.length) return null;

    if (direction > 0 && cause === 'ended' && mode === 'repeat-one' && this.currentId) {
      return this.currentId;
    }

    if (useShuffle) {
      this.ensureShuffle();
      const next = this.shuffledIds[this.shuffledCurrent + 1];
      if (next) {
        this.shuffledCurrent += 1;
        this.lastReturnedId = next;
        return next;
      }
      if (mode === 'repeat-all') {
        this.rebuildShuffle();
        return this.takeNextShuffledAfterRebuild();
      }
      // YesPlayMusic 的随机池耗尽后重新洗牌，避免列表在随机模式下
      // 过早停止；新一轮仍排除当前歌曲。
      this.rebuildShuffle();
      return this.takeNextShuffledAfterRebuild();
    }

    const currentIndex = this.currentId ? this.listIds.indexOf(this.currentId) : -1;
    let nextIndex = currentIndex + direction;
    if (nextIndex >= 0 && nextIndex < this.listIds.length) return this.listIds[nextIndex];
    if (mode === 'repeat-all') {
      nextIndex = direction > 0 ? 0 : this.listIds.length - 1;
      return this.listIds[nextIndex] || null;
    }
    return null;
  }

  /** Return the same next candidate without consuming play-next or shuffle. */
  public peek(options: QueueNextOptions = {}): string | null {
    const direction = options.direction ?? 1;
    const mode = options.mode ?? 'sequence';
    const useShuffle = options.shuffle === true && direction > 0;
    if (direction > 0 && this.playNextIds.length) return this.playNextIds[0] || null;
    if (!this.listIds.length) return null;
    if (direction > 0 && mode === 'repeat-one' && this.currentId) return this.currentId;
    if (useShuffle) {
      this.ensureShuffle();
      const next = this.shuffledIds[this.shuffledCurrent + 1];
      if (next) return next;
      // The next call to `next()` rebuilds the random pool. Do not guess a
      // candidate here: guessing would prefetch a different song than the
      // later shuffle draw and violate the same-identity guarantee.
      return null;
    }
    const currentIndex = this.currentId ? this.listIds.indexOf(this.currentId) : -1;
    let nextIndex = currentIndex + direction;
    if (nextIndex >= 0 && nextIndex < this.listIds.length) return this.listIds[nextIndex];
    if (mode === 'repeat-all') {
      nextIndex = direction > 0 ? 0 : this.listIds.length - 1;
      return this.listIds[nextIndex] || null;
    }
    return null;
  }

  private ensureShuffle(): void {
    if (this.shuffledIds.length !== this.listIds.length || !this.currentId || !this.shuffledIds.includes(this.currentId)) {
      this.rebuildShuffle();
    }
  }

  private rebuildShuffle(): void {
    if (!this.listIds.length) {
      this.shuffledIds = [];
      this.shuffledCurrent = -1;
      return;
    }
    const rest = this.listIds.filter((id) => id !== this.currentId);
    this.shuffledIds = this.currentId ? [this.currentId, ...shuffle(rest, this.random)] : shuffle(this.listIds, this.random);
    this.shuffledCurrent = this.currentId ? 0 : -1;
  }

  private takeNextShuffledAfterRebuild(): string | null {
    const index = this.currentId ? 1 : 0;
    const next = this.shuffledIds[index] || null;
    if (next) {
      this.shuffledCurrent = index;
      this.lastReturnedId = next;
    }
    return next;
  }
}

